import { Link, LinkProps } from '@mui/material';
import { TestIds } from '../../constants';

interface SkipLinkProps extends Omit<LinkProps, 'href'> {
  /**
   * The ID of the element to skip to (without the # prefix)
   * @default 'psp-main-content'
   */
  targetId?: string;
  /**
   * The text displayed in the skip link
   * @default 'Skip to main content'
   */
  children?: React.ReactNode;
}

/**
 * A skip link component that allows keyboard users to bypass navigation
 * and jump directly to the main content.
 *
 * Follows WCAG 2.4.1 - Bypass Blocks (Level A):
 * https://www.w3.org/WAI/WCAG21/Understanding/bypass-blocks.html
 *
 * The link is visually hidden until focused, then appears at the top of the viewport.
 */
/**
 * Scrolls the target element and all nested scrollable elements to the top.
 * Handles remote apps that have their own scroll containers.
 */
function scrollTargetContentToTop(target: HTMLElement): void {
  target.scrollTop = 0;

  target.querySelectorAll('*').forEach((el) => {
    if (el instanceof HTMLElement && el.scrollTop > 0) {
      el.scrollTop = 0;
    }
  });
}

/**
 * Posts a scroll-to-top message to an iframe if present.
 * Used for iframe apps that need to handle their own scrolling.
 */
function postScrollToTopMessage(target: HTMLElement): void {
  const iframe = target.querySelector('iframe');
  if (iframe?.contentWindow && iframe.src) {
    try {
      const targetOrigin = new URL(iframe.src).origin;
      console.info('[SkipLink] Sending psp-scroll-to-top to iframe', targetOrigin);
      iframe.contentWindow.postMessage({ type: 'psp-scroll-to-top' }, targetOrigin);
    } catch {
      console.error('[SkipLink] Invalid iframe URL, skipping postMessage');
    }
  }
}

export function SkipLink({
  targetId = 'psp-main-content',
  children = 'Skip to main content',
  ...props
}: Readonly<SkipLinkProps>) {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const target = document.getElementById(targetId);

    if (target) {
      scrollTargetContentToTop(target);
      postScrollToTopMessage(target);
      target.focus();
    }
  };

  return (
    <Link
      href={`#${targetId}`}
      onClick={handleClick}
      data-testid={TestIds.SKIP_LINK}
      sx={{
        // Visually hidden by default
        position: 'absolute',
        left: '-9999px',
        top: 'auto',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        zIndex: 'tooltip',

        // Visible when focused
        '&:focus': {
          position: 'fixed',
          top: 8,
          left: 8,
          width: 'auto',
          height: 'auto',
          overflow: 'visible',
          padding: 2,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 1,
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          boxShadow: 4,
          outline: '2px solid',
          outlineColor: 'primary.dark',
          outlineOffset: '2px',
        },

        '&:focus:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
      {...props}
    >
      {children}
    </Link>
  );
}
